// ============================================================================
// ORDERS · Collection — the season's repeats, one read (C6)
// ============================================================================
// collection_repeats(p_season_year) answers { ok, season, synced_at, counts,
// repeats[] } — one object per repeat model (the Book's R/RNC rows, the
// decision email, the sell sheets, the Desk PI, the revived link, joined in
// SQL). Same cache shape and rules as useCollectionCards: 60 s stale, refetch
// on focus, a refusal and an unrun package never retried.
// C8 · one entry per line, the store's line unless the caller names one.
// ============================================================================
import { useQuery } from '@tanstack/react-query';
import { fetchCollectionRepeats, lineOf, COLLECTION_SEASON_YEAR } from './api';
import { isMissingFn } from '../desk/useDeskData';
import { COLLECTION_KEY } from './useCollectionCards';
import { useCollectionLine } from './useCollectionLine';
import { LIVE_POLL_MS } from './collectionLive';

export const repeatsKey = (seasonYear, line) => [...COLLECTION_KEY, 'repeats', seasonYear, lineOf(line)];
const isRefusal = (e) => e?.code === '42501' || /42501/.test(String(e?.message || ''));

/** Read state, decided once (Conv #185): failed › loading › ready. */
export function useCollectionRepeats(seasonYear = COLLECTION_SEASON_YEAR, { enabled = true, line: forLine, poll = true } = {}) {
  const { line: storeLine } = useCollectionLine();
  const line = forLine || storeLine;
  const q = useQuery({
    queryKey: repeatsKey(seasonYear, line),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: poll ? LIVE_POLL_MS : false,          // CLB-010 · Home's tile passes poll:false; the world polls · what sends no Realtime event (collectionLive.js)
    refetchIntervalInBackground: false,
    retry: (n, e) => !isMissingFn(e) && !isRefusal(e) && n < 1,
    queryFn: () => fetchCollectionRepeats(seasonYear, line),
  });
  const env = q.data || null;
  return {
    env,
    line,
    repeats: env?.repeats || [],
    counts: env?.counts || null,
    state: q.isError ? 'failed' : q.isPending ? 'loading' : 'ready',
    isError: q.isError, error: q.error,
    notDeployed: q.isError && isMissingFn(q.error),
    refused: q.isError && isRefusal(q.error),
    refetch: q.refetch,
  };
}
